import { computed } from 'vue'
import { defineStore } from 'pinia'

import type { PdfExportOptions } from '@/views/Dashboards/Dashboard/export/types'

import { ANALYTICS_DEFAULT_DECIMAL_PRECISIONS } from './utils/const'

import { useUISettings } from '@/hooks/uiSettings'

export const useSettingsStore = defineStore('settings', () => {
  // INIT

  const gridExtraColumns = useUISettings<boolean>(
    ['common', 'grid-show-extra-columns'],
    false,
  )

  const analyticsDecimalPrecisions = useUISettings<number>(
    ['analytics', 'decimal-precisions'],
    ANALYTICS_DEFAULT_DECIMAL_PRECISIONS,
  )

  const ownershipAdjustedValues = useUISettings<boolean>(
    ['analytics', 'ownership-adjusted-values'],
    false,
  )

  const aIAgent = useUISettings<boolean>(['common', 'ai-agent'], false)

  const pdfExport = useUISettings<boolean>(['common', 'pdf-export'], false)
  const pdfExportOptions = useUISettings<Partial<PdfExportOptions> | undefined>(
    ['common', 'pdf-export-options'],
    undefined,
  )
  const pdfExportPreview = useUISettings<boolean>(['common', 'pdf-export-preview'], false)

  const powerMode = useUISettings<boolean>(['common', 'power-mode'], false)

  // GETTERS

  const getGridExtraColumns = computed(() => gridExtraColumns.value)
  const getAIAgent = computed(() => aIAgent.value)
  const getPdfExport = computed(() => pdfExport.value)
  const getPdfExportOptions = computed(() => pdfExportOptions.value)
  const getPdfExportPreview = computed(() => pdfExportPreview.value)
  const getAnalyticsDecimalPrecisions = computed(() => analyticsDecimalPrecisions.value)
  const getOwnershipAdjustedValues = computed(() => ownershipAdjustedValues.value)
  const getPowerMode = computed(() => powerMode.value)

  // SETTERS

  const setGridExtraColumns = (value: boolean) => {
    gridExtraColumns.value = value
  }

  const setAIAgent = (value: boolean) => {
    aIAgent.value = value
  }

  const setPdfExport = (value: boolean) => {
    pdfExport.value = value
  }

  const setPdfExportOptions = (value: PdfExportOptions) => {
    pdfExportOptions.value = value
  }

  const setPdfExportPreview = (value: boolean) => {
    pdfExportPreview.value = value
  }

  const setAnalyticsDecimalPrecisions = (value: number) => {
    analyticsDecimalPrecisions.value = value
  }

  const setOwnershipAdjustedValues = (value: boolean) => {
    ownershipAdjustedValues.value = value
  }

  const setPowerMode = (value: boolean) => {
    powerMode.value = value
  }

  return {
    getGridExtraColumns,
    getAIAgent,
    getPdfExport,
    getPdfExportOptions,
    getPdfExportPreview,
    getAnalyticsDecimalPrecisions,
    getOwnershipAdjustedValues,
    getPowerMode,

    setGridExtraColumns,
    setAIAgent,
    setPdfExport,
    setPdfExportOptions,
    setPdfExportPreview,
    setAnalyticsDecimalPrecisions,
    setOwnershipAdjustedValues,
    setPowerMode,
  }
})
